import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  fetchExecutiveRequestDetails,
  approveExecutiveRequest,
  attestExecutiveRequest,
  verifyExecutiveRequest,
  rejectExecutiveRequest,
  payExecutiveRequest,
  queryExecutiveRequestComment,
  fetchExecutiveRequestVouchers,
} from "../../../../contents/redux/actions/executiveRequestActions";
import { fetchWorkflowRouteActors } from "../../../../contents/redux/actions/workflowRouteActions";
import styles from "../../../utilities/cssmodules/ViewExecutiveRequest.module.css";
import {
  CalendarMonthRounded,
  DoneAllRounded,
  DoneRounded,
  DoNotDisturbAltRounded,
  EditRounded,
  HelpOutlineRounded,
  LiveHelpRounded,
  LowPriorityRounded,
  MonetizationOnRounded,
  PaymentsRounded,
  PersonRounded,
  PriceCheckRounded,
  TaskAltRounded,
} from "@mui/icons-material";
import { Tooltip } from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import QueryModal from "../payment/QueryModal";
import VoucherModal from "./VoucherModal";
import PayModal from "../payment/PayModal";
import { toast } from "react-toastify";

const ViewExecutiveRequest = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { requestId, requestType, requester, createdOn } = location.state || {};

  const requestDetails = useSelector(
    (state) => state.executiveRequest.executiveDetails || null,
  );
  const executiveVouchers = useSelector(
    (state) => state.executiveRequest.executiveVouchers || null,
  );
  const isLoading = useSelector((state) => state.executiveRequest.isLoading);

  const [userPermissions, setUserPermissions] = useState([]);
  const [isQueryModalOpen, setIsQueryModalOpen] = useState(false);
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [isVoucherModalOpen, setIsVoucherModalOpen] = useState(false);
  const [isPayModalOpen, setIsPayModalOpen] = useState(false);
  const [workflowId, setWorkflowId] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [isLoadingVoucher, setIsLoadingVoucher] = useState(false);

  // Go back to previous component
  const goBack = () => {
    navigate(-1);
  };

  const loggedInUserRole = localStorage.getItem("loggedInUserRole");

  useEffect(() => {
    console.log(
      "ViewExecutiveRequest - Mounting with requestId:",
      requestId,
      "requestType:",
      requestType,
    );
    if (!requestId || !requestType) {
      console.warn("Missing requestId or requestType:", {
        requestId,
        requestType,
      });
      toast.error("Invalid request ID or type.");
      navigate("/pendingrequests");
      return;
    }
    dispatch(fetchExecutiveRequestDetails(requestId));
    return () => console.log("ViewExecutiveRequest - Cleanup on unmount");
  }, [requestId, requestType, dispatch, navigate]);

  useEffect(() => {
    if (requestDetails) {
      const id = requestDetails?.workflowPermissions?.[0]?.workflowId;
      setWorkflowId(id);
      console.log("ViewExecutiveRequest - Workflow ID:", id);

      const loggedInUserId = parseInt(
        localStorage.getItem("loggedInUserId"),
        10,
      );
      const loggedInUserRole = localStorage
        .getItem("loggedInUserRole")
        ?.toLowerCase();
      console.log(
        "ViewExecutiveRequest - Logged-in User ID:",
        loggedInUserId,
        "Role:",
        loggedInUserRole,
      );
      console.log(
        "ViewExecutiveRequest - Current Role:",
        requestDetails.currentRole,
      );

      const isStaff = loggedInUserRole === "staff";
      const permissions = !isStaff
        ? requestDetails?.workflowPermissions
            ?.filter((perm) => perm.roleId === requestDetails?.currentRole)
            ?.map((perm) => perm.permissionName) || []
        : [];
      setUserPermissions(permissions);
      console.log("ViewExecutiveRequest - User Permissions:", permissions);
    }
  }, [requestDetails]);

  useEffect(() => {
    if (workflowId) {
      console.log(
        "ViewExecutiveRequest - Fetching workflow actors for workflowId:",
        workflowId,
      );
      dispatch(fetchWorkflowRouteActors(workflowId));
    }
  }, [workflowId, dispatch]);

  useEffect(() => {
    console.log("ViewExecutiveRequest - executiveVouchers:", executiveVouchers);
    if (executiveVouchers?.url) {
      console.log(
        "ViewExecutiveRequest - Setting pdfUrl:",
        executiveVouchers.url,
      );
      setPdfUrl(executiveVouchers.url);
      setIsVoucherModalOpen(true);
    }
    return () => {
      if (pdfUrl) {
        console.log("ViewExecutiveRequest - Cleanup, revoking pdfUrl:", pdfUrl);
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [executiveVouchers, pdfUrl]);

  const handleVoucherModal = async () => {
    if (!requestId || !requestDetails?.paymentCode) {
      console.error("ViewExecutiveRequest - Missing requestId or paymentCode");
      toast.error("Request ID or payment code is missing.");
      return;
    }
    console.log(
      "ViewExecutiveRequest - Fetching voucher - requestId:",
      requestId,
      "paymentCode:",
      requestDetails.paymentCode,
    );
    setIsLoadingVoucher(true);
    try {
      const payload = await dispatch(
        fetchExecutiveRequestVouchers(
          requestId,
          requestDetails.paymentCode,
          "Receipt",
        ),
      );
      console.log("ViewExecutiveRequest - Voucher fetch successful:", payload);
      if (payload?.url) {
        setPdfUrl(payload.url);
        setIsVoucherModalOpen(true);
      }
    } catch (error) {
      console.error(
        "ViewExecutiveRequest - Failed to fetch voucher:",
        error.message,
      );
      toast.error("Failed to fetch voucher: " + error.message);
    } finally {
      setIsLoadingVoucher(false);
    }
  };

  const handleQueryModal = () => {
    console.log("ViewExecutiveRequest - Opening QueryModal");
    setIsQueryModalOpen(true);
  };

  const handleApproveModal = () => {
    console.log("ViewExecutiveRequest - Opening QueryModal");
    setIsApproveModalOpen(true);
  };

  const handlePayModal = () => {
    console.log("ViewExecutiveRequest - Opening PayModal");
    setIsPayModalOpen(true);
  };

  const handleQuerySubmit = (props) => {
    const userId = parseInt(localStorage.getItem("loggedInUserId"), 10);
    const requestTypeId = requestDetails?.requestTypeId;
    const isRequester = props.selectedActor?.value.includes("requester-");
    const payload = {
      notes: props.notes,
      userId,
      requestTypeId,
      requestId,
      targetRoleId: isRequester ? null : props.selectedActor?.role,
      targetUserId: isRequester ? props.selectedActor?.role : null,
      model: "ExecutiveRequest",
    };
    dispatch(queryExecutiveRequestComment(payload))
      .then(() => {
        if (loggedInUserRole === "Top Management") {
          navigate("/allrequests");
        } else {
          navigate("/pendingrequests");
        }
      })
      .catch((error) => {
        console.error(
          "ViewExecutiveRequest - Error querying request:",
          error.message,
        );
        toast.error("Failed to query request: " + error.message);
      });
  };

  const handlePaySubmit = ({ requestId, paymentCode, requiresReceipt }) => {
    const userId = parseInt(localStorage.getItem("loggedInUserId"), 10);
    if (!userId) {
      toast.error("User ID not found. Please log in again.");
      return;
    }
    if (!requestDetails?.paymentCode) {
      toast.error("No payment code associated with this request.");
      return;
    }
    if (
      parseInt(paymentCode, 10) !== parseInt(requestDetails.paymentCode, 10)
    ) {
      toast.error("Invalid payment code.");
      return;
    }
    dispatch(
      payExecutiveRequest(requestId, paymentCode, requiresReceipt, userId),
    )
      .then(() => {
        if (loggedInUserRole === "Top Management") {
          navigate("/allrequests");
        } else {
          navigate("/pendingrequests");
        }
      })
      .catch((error) => {
        console.error(
          "ViewExecutiveRequest - Error paying request:",
          error.message,
        );
        toast.error("Failed to pay request: " + error.message);
      });
    setIsPayModalOpen(false);
  };

  const handleAction = (actionFn, successMessage, errorMessage) => {
    if (!requestId) {
      toast.error("Request ID is missing.");
      return;
    }
    dispatch(actionFn(requestId))
      .then(() => {
        if (loggedInUserRole === "Top Management") {
          navigate("/allrequests");
        } else {
          navigate("/pendingrequests");
        }
      })
      .catch((error) => {
        console.error(`ViewExecutiveRequest - ${errorMessage}:`, error.message);
        toast.error(errorMessage + ": " + error.message);
      });
  };

  if (isLoading) {
    return <div>Loading request details...</div>;
  }

  if (!requestDetails) {
    return <div>No request details available.</div>;
  }

  return (
    <div className={styles["component-wrapper"]}>
      <div className={styles["component-title"]}>
        <div className={styles["flex-row-container"]}>
          <div className={styles["flex-row-custom-1"]}>
            <span
              className={styles["title-back-button"]}
              onClick={goBack}
              aria-label="Go Back"
            >
              <Tooltip title="Go Back" arrow placement="top">
                <ArrowBackIosIcon />
              </Tooltip>
            </span>
            <span>View {requestType} Request</span>
          </div>
        </div>
      </div>
      <div className={styles["wrapper"]}>
        <div className={styles["generic_request_content_component_wrapper"]}>
          <div className={styles["generic_request_content_wrapper"]}>
            <div className={styles["request-details-header"]}>
              <div className={styles["section-header"]}>Request Details</div>
              <div className={styles["status-container"]}>
                <p>
                  Status:{" "}
                  <span className={styles["status-badge"]}>
                    {requestDetails?.status === "Unknown Status"
                      ? "Archived"
                      : requestDetails?.status || "Unknown"}
                  </span>
                </p>
              </div>
            </div>
            <div className={styles["generic_request_content_container"]}>
              <div className={styles["generic_request_content"]}>
                <div className={styles["generic_request_content_heading"]}>
                  <PersonRounded /> Requester:
                </div>
                <div className={styles["generic_request_content_data"]}>
                  {requester || "N/A"}
                </div>
              </div>
              <div className={styles["generic_request_content"]}>
                <div className={styles["generic_request_content_heading"]}>
                  <CalendarMonthRounded /> Date created:
                </div>
                <div className={styles["generic_request_content_data"]}>
                  {createdOn
                    ? new Date(createdOn).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })
                    : "N/A"}
                </div>
              </div>
              <div className={styles["generic_request_content"]}>
                <div className={styles["generic_request_content_heading"]}>
                  <MonetizationOnRounded /> Amount requested:
                </div>
                <div className={styles["generic_request_content_data"]}>
                  {requestDetails?.amount ? `$${requestDetails.amount}` : "N/A"}
                </div>
              </div>
              <div className={styles["generic_request_content"]}>
                <div className={styles["generic_request_content_heading"]}>
                  <PaymentsRounded /> Currency:
                </div>
                <div className={styles["generic_request_content_data"]}>
                  {requestDetails?.currency || "N/A"}
                </div>
              </div>
              <div className={styles["generic_request_content"]}>
                <div className={styles["generic_request_content_heading"]}>
                  <LiveHelpRounded /> Purpose of request:
                </div>
                <div className={styles["generic_request_content_data"]}>
                  {requestDetails?.purpose || "No purpose provided"}
                </div>
              </div>
              <div className={styles["generic_request_content"]}>
                <div className={styles["generic_request_content_heading"]}>
                  <EditRounded /> Comment:
                </div>
                <div className={styles["generic_request_content_data"]}>
                  {requestDetails?.comments?.length > 0 ? (
                    <div className={styles["comment_block"]}>
                      <p>
                        <strong>Comment:</strong>{" "}
                        {
                          requestDetails.comments[
                            requestDetails.comments.length - 1
                          ].notes
                        }
                      </p>
                      <p className={styles["comment-meta"]}>
                        <strong>Queried By:</strong>{" "}
                        {
                          requestDetails.comments[
                            requestDetails.comments.length - 1
                          ].queriedBy
                        }
                      </p>
                    </div>
                  ) : (
                    "No comments available"
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className={styles["generic_request_actions_wrapper"]}>
            <div className={styles["actions-header"]}>
              <div className={styles["section-header"]}>Actions</div>
              <button
                onClick={handleVoucherModal}
                className={styles["view-voucher-button"]}
                disabled={isLoadingVoucher}
                aria-label="View Voucher"
              >
                {isLoadingVoucher ? "Loading..." : "View Voucher"}
              </button>
            </div>
            {requestDetails?.status !== "Paid" && (
              <div className={styles["generic_request_actions"]}>
                {userPermissions.includes("Query") && (
                  <Tooltip
                    title="Question or recall due to dissatisfaction"
                    arrow
                    placement="top"
                  >
                    <button
                      onClick={handleQueryModal}
                      className={styles["generic_request_button_secondary"]}
                      aria-label="Query request"
                    >
                      <HelpOutlineRounded /> Query
                    </button>
                  </Tooltip>
                )}
                {userPermissions.includes("Pay") && (
                  <Tooltip title="Initiate payment" arrow placement="top">
                    <button
                      onClick={handlePayModal}
                      className={styles["generic_request_button_primary"]}
                      aria-label="Pay request"
                    >
                      <PriceCheckRounded /> Pay
                    </button>
                  </Tooltip>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <QueryModal
        open={isQueryModalOpen}
        onClose={() => setIsQueryModalOpen(false)}
        onSubmit={handleQuerySubmit}
        workflowId={workflowId}
        requester={requester}
        requesterId={requestDetails?.requesterId}
      />
      <VoucherModal
        open={isVoucherModalOpen}
        onClose={() => {
          console.log(
            "ViewExecutiveRequest - Closing VoucherModal, revoking pdfUrl:",
            pdfUrl,
          );
          if (pdfUrl) URL.revokeObjectURL(pdfUrl);
          setIsVoucherModalOpen(false);
          setPdfUrl(null);
        }}
        requestId={requestId}
        pdfUrl={pdfUrl}
        paymentCode={requestDetails?.paymentCode}
        requestType={requestType || "Executive"}
      />
      <PayModal
        open={isPayModalOpen}
        onClose={() => setIsPayModalOpen(false)}
        onSubmit={handlePaySubmit}
        requestId={requestId}
      />
    </div>
  );
};

export default ViewExecutiveRequest;
